import type { EchoflicksSessionEvent } from "../types";

export type PortraitRetakeSubjectMode = "person" | "non_person";

export type PortraitPersonRetakeLineage = {
  file: string;
  mediaId: string;
  modelId: string;
  provider: string;
  promptVersion: string;
  deliveryAspect: "4:3";
  outputWidth: number;
  outputHeight: number;
  outputSha256: string;
  /**
   * Which gate proved the retake faithful. The person lane compares faces; the
   * faceless lane (pets, landscapes, objects) requires Rekognition subject
   * labels to survive and forbids an introduced person.
   */
  subjectMode: PortraitRetakeSubjectMode;
  qc: "identity" | "subject";
  subjectLabels: string[];
};

const STORAGE_PREFIX_BY_MODE: Record<PortraitRetakeSubjectMode, string> = {
  person: "derived/portrait-person-retake/",
  non_person: "derived/portrait-subject-retake/",
};

export type PortraitPersonRetakeIndex = {
  byMediaId: Map<string, PortraitPersonRetakeLineage>;
  byFile: Map<string, PortraitPersonRetakeLineage | null>;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function positiveInteger(value: unknown): number | null {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function parseLineage(value: unknown): PortraitPersonRetakeLineage | null {
  if (!isRecord(value)) return null;
  const file = stringValue(value.file);
  const mediaId = stringValue(value.media_id);
  const modelId = stringValue(value.model_id);
  const provider = stringValue(value.provider);
  const promptVersion = stringValue(value.prompt_version);
  const deliveryAspect = stringValue(value.delivery_aspect);
  const outputWidth = positiveInteger(value.output_width);
  const outputHeight = positiveInteger(value.output_height);
  const outputSha256 = stringValue(value.output_sha256);
  const storageKey = stringValue(value.storage_key);

  // Older events predate the subject lane and carry no subject_mode; they are
  // person retakes by construction.
  const rawSubjectMode = stringValue(value.subject_mode) ?? "person";
  if (rawSubjectMode !== "person" && rawSubjectMode !== "non_person")
    return null;
  const subjectMode: PortraitRetakeSubjectMode = rawSubjectMode;

  // Each lane must present its own passing gate; neither substitutes the other.
  const qc = subjectMode === "person" ? "identity" : "subject";
  const gate =
    subjectMode === "person"
      ? stringValue(value.identity_qc)
      : stringValue(value.subject_qc);

  const subjectLabels = Array.isArray(value.subject_labels)
    ? value.subject_labels.flatMap((label) => {
        const parsed = stringValue(label);
        return parsed ? [parsed] : [];
      })
    : [];

  if (
    !file ||
    !mediaId ||
    !modelId ||
    !provider ||
    !promptVersion ||
    deliveryAspect !== "4:3" ||
    outputWidth == null ||
    outputHeight == null ||
    !outputSha256 ||
    gate !== "passed" ||
    !storageKey?.startsWith(STORAGE_PREFIX_BY_MODE[subjectMode]) ||
    (subjectMode === "non_person" && subjectLabels.length === 0)
  ) {
    return null;
  }

  return {
    file,
    mediaId,
    modelId,
    provider,
    promptVersion,
    deliveryAspect,
    outputWidth,
    outputHeight,
    outputSha256,
    subjectMode,
    qc,
    subjectLabels,
  };
}

/**
 * Build accepted per-image lineage from append-only orchestrator events.
 * Policy intent, failed-open events, and incomplete legacy payloads never earn
 * a normalization badge.
 */
export function buildPortraitPersonRetakeIndex(
  events: EchoflicksSessionEvent[] | undefined,
): PortraitPersonRetakeIndex {
  const byMediaId = new Map<string, PortraitPersonRetakeLineage>();
  const byFile = new Map<string, PortraitPersonRetakeLineage | null>();

  for (const event of events ?? []) {
    if (event.event_type !== "script.portrait_person_retake_applied") continue;
    const payload = isRecord(event.raw_payload) ? event.raw_payload : {};
    if (stringValue(payload.status) !== "applied") continue;
    const items = Array.isArray(payload.items) ? payload.items : [];
    for (const item of items) {
      const lineage = parseLineage(item);
      if (!lineage) continue;
      byMediaId.set(lineage.mediaId, lineage);
      if (!byFile.has(lineage.file)) {
        byFile.set(lineage.file, lineage);
      } else if (byFile.get(lineage.file)?.mediaId !== lineage.mediaId) {
        // Filename-only matching is permitted only when unique within the Echo.
        byFile.set(lineage.file, null);
      }
    }
  }

  return { byMediaId, byFile };
}

export function portraitPersonRetakeForSource(
  index: PortraitPersonRetakeIndex,
  source: { sourceMediaId: string | null; sourceFile: string | null } | null,
): PortraitPersonRetakeLineage | null {
  if (!source) return null;
  if (source.sourceMediaId) {
    const exact = index.byMediaId.get(source.sourceMediaId);
    if (exact) return exact;
  }
  return source.sourceFile
    ? (index.byFile.get(source.sourceFile) ?? null)
    : null;
}
